import { authApi } from 'api';

/**
 * Create a new club by sending a POST request to the '/Clubs' endpoint.
 *
 * @param {Object} clubData - The data of the club to be created.
 * @returns {Promise} - The response from the API.
 */
export const createClub = async (clubData) => {
  return await authApi.post('/Clubs', clubData);
};

/**
 * Fetch the list of all clubs by sending a GET request to the '/Clubs' endpoint.
 *
 * @returns {Promise} - The response from the API containing the list of clubs.
 */
export const getClubs = async () => {
  return await authApi.get('/Clubs');
};

/**
 * Fetch the list of meets for a specific club by sending a GET request to the '/meets/{clubId}' endpoint.
 * Optionally, you can pass the 'lastUpdated' parameter to filter meets after a specific time.
 *
 * @param {string} clubId - The ID of the club whose meets are to be fetched.
 * @param {string|null} lastUpdated - The optional timestamp to filter meets by their last update time.
 * @returns {Promise} - The response from the API containing the list of meets.
 */
export const getMeets = async (clubId, lastUpdated = null) => {
  if (!lastUpdated) {
    return await authApi.get(`/meets/${clubId}`);
  } else {
    return await authApi.get(`/meets/${clubId}?lastUpdated=${lastUpdated}`);
  }
};

/**
 * Fetch the list of recent meets for a specific club by sending a GET request to the '/meets/recent/{clubId}' endpoint.
 * Optionally, you can pass the 'lastUpdated' parameter to filter meets after a specific time.
 * @deprecated
 * @param {string} clubId - The ID of the club whose recent meets are to be fetched.
 * @param {string|null} lastUpdated - The optional timestamp to filter meets by their last update time.
 * @returns {Promise} - The response from the API containing the list of recent meets.
 */
export const getRecentMeets = async (clubId, lastUpdated = null) => {
  if (!lastUpdated) {
    return await authApi.get(`/meets/recent/${clubId}`);
  } else {
    return await authApi.get(
      `/meets/recent/${clubId}?lastUpdated=${lastUpdated}`
    );
  }
};

/**
 * Fetch the list of upcoming meets for a specific club by sending a GET request to the '/meets/upcoming/{clubId}' endpoint.
 * Optionally, you can pass the 'lastUpdated' parameter to filter meets after a specific time.
 * @deprecated
 * @param {string} clubId - The ID of the club whose upcoming meets are to be fetched.
 * @param {string|null} lastUpdated - The optional timestamp to filter meets by their last update time.
 * @returns {Promise} - The response from the API containing the list of upcoming meets.
 */
export const getUpcomingMeets = async (clubId, lastUpdated = null) => {
  if (!lastUpdated) {
    return await authApi.get(`/meets/upcoming/${clubId}`);
  } else {
    return await authApi.get(
      `/meets/upcoming/${clubId}?lastUpdated=${lastUpdated}`
    );
  }
};

/**
 * Fetch the details of a specific result by sending a GET request to the '/meets/{clubId}/{meetId}' endpoint.
 *
 * @param {string} clubId - The ID of the club associated with the result.
 * @param {string} meetId - The ID of the result to be fetched.
 * @returns {Promise} - The response from the API containing the details of the specified result.
 */
export const getMeetById = async (clubId, meetId) => {
  return await authApi.get(`/meets/${clubId}/${meetId}`);
};

// Slim meet summary used by results / events pages — returns only meetID,
// clubID, meetName, meetDate, hasAgeEvents.
export const getMeetSummary = async (clubId, meetId) => {
  return await authApi.get(`/meets/${clubId}/${meetId}/summary`);
};

/**
 * Fetches athletes for a specific club, optionally filtered by a last updated timestamp.
 *
 * This function retrieves a list of athletes for a given club. If a `lastUpdated` timestamp
 * is provided, it will only return athletes who have been updated since that timestamp.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string|null} lastUpdated - An optional timestamp to filter athletes by their last update date (format: YYYY-MM-DDTHH:mm:ss).
 * @returns {Promise<Object>} - A Promise that resolves to the response from the API containing athletes.
 */
export const getAthletes = async (clubId, lastUpdated = null) => {
  if (!lastUpdated) {
    return await authApi.get(`/users/athletes/${clubId}`);
  } else {
    return await authApi.get(
      `/users/athletes/${clubId}?lastUpdated=${lastUpdated}`
    );
  }
};

/**
 * Creates a new result.
 *
 * This function sends a request to create a new result with the provided information.
 *
 * @param {Object} meetInfo - An object containing the details of the result to be created (e.g., name, date, location).
 * @returns {Promise<Object>} - A Promise that resolves to the API response after creating the result.
 */
export const createMeet = async (meetInfo) => {
  return await authApi.post(`/Meets`, meetInfo);
};

/**
 * Updates an existing result's details.
 *
 * This function sends a request to update the information of an existing result identified by its unique ID.
 *
 * @param {string} meetId - The unique identifier of the result to be updated.
 * @param {Object} meetInfo - An object containing the updated result details (e.g., name, date, location).
 * @returns {Promise<Object>} - A Promise that resolves to the API response after updating the result.
 */
export const updateMeet = async (meetId, meetInfo) => {
  return await authApi.put(`/meets/${meetId}`, meetInfo);
};

/**
 * Retrieves filtered meets based on specific criteria.
 *
 * This function sends a request to fetch a list of meets that match the provided filters, including club ID, season ID, and meet statuses.
 *
 * @param {string} clubID - The unique identifier of the club to filter meets for.
 * @param {Array<string>} meetStatuses - An array of meet statuses to filter by (e.g., "Complete", "Upcoming", "Draft").
 * @param {string|null} [seasonID=null] - An optional season ID to filter meets by season.
 * @param {string|null} [lastUpdated=null] - An optional ISO timestamp to filter meets updated after this time.
 * @returns {Promise<Object>} - A Promise that resolves to the API response containing the filtered list of meets.
 */
export const getFilteredMeets = async (
  clubID,
  meetStatuses,
  seasonID = null,
  lastUpdated = null
) => {
  const payload = {
    clubID,
    meetStatuses,
    lastUpdated,
  };

  if (seasonID) {
    payload.seasonID = seasonID;
  }

  return await authApi.post(`/meets/get-filtered-meets`, payload);
};

/**
 * Retrieves a slim list of meets for a club's season for use on the meets list page.
 *
 * Returns only the fields the meets list view consumes
 * (meetID, meetName, meetDate, location, ageGroups, countsForSeasonPoints, countsForSeasonBests, meetType, meetStatus).
 * When seasonID is omitted, the server resolves the club's currently Active season.
 * For users below Official permission, Draft meets are excluded by the server.
 *
 * @param {string} clubID - The unique identifier of the club.
 * @param {string|null} [seasonID=null] - Optional season ID; defaults to the club's active season.
 * @returns {Promise<Object>} - API response containing the season's meets.
 */
export const getSeasonMeets = async (clubID, seasonID = null) => {
  const payload = { clubID };
  if (seasonID) {
    payload.seasonID = seasonID;
  }
  return await authApi.post(`/meets/get-season-meets`, payload);
};

/**
 * Fetches club records with specified age groups.
 *
 * @param {string} clubId - The ID of the club.
 * @param {string} eventType - The type of event.
 * @param {string} ageGroupsQuery - The age groups to filter the records.
 * @param {string} gender - The gender category.
 * @returns {Promise} - A promise that resolves with the club records.
 */
export const getClubRecordsWithAgeGroups = async (
  clubId,
  eventType,
  ageGroupsQuery,
  gender
) => {
  return await authApi.post(`/clubRecords/get-club-records-ages-query`, {
    clubId,
    eventType,
    ageGroupsQuery,
    gender,
  });
};

export const getAllClubRecords = async (clubId) => {
  return await authApi.get(`/clubRecords/all?clubId=${clubId}`);
};

export const getPendingClubRecords = async (clubId) => {
  return await authApi.get(`/clubRecords/pending?clubId=${clubId}`);
};

export const getPendingClubRecordsCount = async (clubId) => {
  return await authApi.get(`/ClubRecords/pending/count?clubId=${clubId}`);
};

// Update Pending Records: checks the current season's results against the
// club records now (the season replay does it within an hour of a change).
// Admins only. Replies { marked, cleared }.
export const updatePendingClubRecords = async (clubId) => {
  return await authApi.post(`/ClubRecords/update-pending?clubId=${clubId}`);
};

export const updateStatusOfPendingClubRecordResult = async (
  clubId,
  resultId,
  newStatus,
  approvedBy
) => {
  return await authApi.post(`/ClubRecords/update-status`, {
    clubId,
    resultId,
    newStatus,
    approvedBy,
  });
};

// Set this to true to enable dry run mode (logs only, no API calls)
const DRY_RUN_MODE = false;

/**
 * Add a new current club record
 *
 * @param {Object} recordData - The club record data
 * @param {string} recordData.clubId - The ID of the club
 * @param {string} recordData.eventType - The type of event
 * @param {number} recordData.athleteAge - The age of the athlete
 * @param {string} recordData.gender - The gender category
 * @param {string} recordData.trackSurface - The track surface type
 * @param {string} recordData.athleteFirstName - Athlete's first name
 * @param {string} recordData.athleteLastName - Athlete's last name
 * @param {number} recordData.recordValue - The record value
 * @param {string} recordData.achievedAt - When the record was achieved (ISO date)
 * @param {string} recordData.approvedBy - Who approved the record
 * @param {string} recordData.approvedDate - When the record was approved (ISO date)
 * @returns {Promise} - The response from the API
 */
export const addClubRecord = async (recordData) => {
  console.log('ADD CLUB RECORD REQUEST:', JSON.stringify(recordData, null, 2));

  if (DRY_RUN_MODE) {
    console.log('🔸 DRY RUN MODE - Request not sent to server');
    return Promise.resolve({ data: { success: true, dryRun: true } });
  }

  return await authApi.post('/ClubRecords/add', recordData);
};

/**
 * Edit an existing current club record
 *
 * @param {string} recordId - The ID of the record to edit
 * @param {Object} recordData - The updated record data
 * @param {string} recordData.athleteFirstName - Athlete's first name
 * @param {string} recordData.athleteLastName - Athlete's last name
 * @param {number} recordData.recordValue - The record value
 * @param {string} recordData.achievedAt - When the record was achieved (ISO date)
 * @param {string} recordData.trackSurface - The track surface type
 * @param {string} recordData.approvedBy - Who approved the record
 * @returns {Promise} - The response from the API
 */
export const editClubRecord = async (recordId, recordData) => {
  // console.log(
  //   `EDIT CLUB RECORD REQUEST (ID: ${recordId}):`,
  //   JSON.stringify(recordData, null, 2)
  // );

  if (DRY_RUN_MODE) {
    console.log('🔸 DRY RUN MODE - Request not sent to server');
    return Promise.resolve({ data: { success: true, dryRun: true } });
  }

  return await authApi.put(`/ClubRecords/edit/${recordId}`, recordData);
};

/**
 * Delete a current club record
 *
 * @param {string} recordId - The ID of the record to delete
 * @returns {Promise} - The response from the API
 */
export const deleteClubRecord = async (recordId) => {
  console.log(`DELETE CLUB RECORD REQUEST (ID: ${recordId})`);

  if (DRY_RUN_MODE) {
    console.log('🔸 DRY RUN MODE - Request not sent to server');
    return Promise.resolve({ data: { success: true, dryRun: true } });
  }

  return await authApi.delete(`/ClubRecords/${recordId}`);
};

/**
 * Fetch meet defaults for a specific club when creating a new meet.
 *
 * @param {string} clubId - The ID of the club to fetch defaults for.
 * @returns {Promise} - The response from the API containing meet defaults.
 */
export const getMeetDefaults = async (clubId) => {
  return await authApi.get(`/Meets/${clubId}/meet-defaults`);
};

// Fetches club settings for a specific club by its ID.
export const getClubSettings = async (clubId) => {
  try {
    const response = await authApi.get(`/clubs/${clubId}/settings`);
    return response;
  } catch (err) {
    throw err;
  }
};

/*
** Settings example value - 
{
  "clubName": "string",
  "clubLocation": "string",
  "clubDescription": "string",
  "adminPIN": "string",
  "defaultNewMeetType": 1,
  "defaultTimezone": "UTC",
  "defaultStartTime": "14:00",
  "defaultLocation": "string",
  "meetParticipatePoints": 1,
  "eventParticipatePoints": 1,
  "firstPlacePoints": 6,
  "secondPlacePoints": 5,
  "thirdPlacePoints": 4,
  "fourthPlacePoints": 3,
  "fifthPlacePoints": 2,
  "setSeasonBestPoints": 2,
  "equalSeasonBestPoints": 1,
  "beatSeasonPBPoints": 1,
  "excludeFirstPBFromPoints": false
}
*/
export const updateClubSettings = async (clubId, settings) => {
  try {
    const response = await authApi.put(`/clubs/${clubId}/settings`, settings);
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Updates the club's PIN code by sending a PUT request to the '/Clubs/{clubId}/pin' endpoint.
 *
 * @param {string} clubId - The ID of the club whose PIN is to be updated.
 * @param {string} currentPIN - The current PIN for verification.
 * @param {string} newPIN - The new PIN to be set.
 * @returns {Promise} - The response from the API.
 */
export const updateClubPin = async (clubId, currentPIN, newPIN) => {
  try {
    const response = await authApi.put(`/Clubs/${clubId}/pin`, {
      currentPIN,
      newPIN,
    });
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Requests the server to email the current Admin PIN to the authenticated
 * admin's registered email address.
 *
 * @param {string} clubId - The ID of the club whose PIN should be emailed.
 * @returns {Promise} - The response from the API.
 */
export const emailClubPin = async (clubId) => {
  return await authApi.post(`/clubs/${clubId}/pin/email`);
};

/**
 * Validates a PIN against the server
 * @param {string} clubId - The club ID
 * @param {string} pin - The PIN to validate
 * @returns {Promise} - The response from the API
 */
export const validatePin = async (clubId, pin) => {
  try {
    const response = await authApi.post(`/Clubs/${clubId}/pin/validate`, {
      pin: pin,
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getDashboard = async (clubId) => {
  try {
    const response = await authApi.get(`/Clubs/dashboard?clubId=${clubId}`);
    return response?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Get club age groups
 * @param {string} clubId - The club ID
 * @returns {Promise} - The response from the API
 */
export const getClubProgramGroups = async (clubId) => {
  try {
    const response = await authApi.get(
      `/Clubs/${clubId}/groups/program-groups`
    );
    return response;
  } catch (err) {
    throw err;
  }
};
